import React from 'react';
import { View, Modal, Pressable, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { SubHeading, Caption } from '@/components/ui/Text';

const isAndroid = Platform.OS === 'android';

type NewTripOptionsModalProps = {
  visible: boolean;
  onClose: () => void;
  onPlanManually: () => void;
  onPlanWithAI: () => void;
  onConnectEmail: () => void;
  onSmartUpload: () => void;
};

export function NewTripOptionsModal({
  visible,
  onClose,
  onPlanManually,
  onPlanWithAI,
  onConnectEmail,
  onSmartUpload,
}: NewTripOptionsModalProps) {
  const theme = useTheme();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: theme.colors.overlay,
          justifyContent: 'center',
          padding: theme.spacing(2.5),
        }}
        onPress={onClose}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={[
            styles.glassCard,
            {
              borderRadius: theme.radii.lg,
              padding: theme.spacing(2),
              gap: theme.spacing(1),
              borderColor:
                theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.6)',
              maxWidth: 420,
              ...theme.shadows.level2,
              // Solid background on Android so text doesn't blend with content behind (BlurView is more transparent there)
              ...(isAndroid ? { backgroundColor: theme.colors.surfaceElevated ?? theme.colors.surface } : {}),
            },
          ]}
        >
          {isAndroid ? null : (
            <>
              <BlurView
                intensity={24}
                tint={theme.colorScheme === 'dark' ? 'dark' : 'light'}
                style={[StyleSheet.absoluteFillObject, { borderRadius: theme.radii.lg }]}
              />
              <View
                pointerEvents="none"
                style={[
                  StyleSheet.absoluteFillObject,
                  {
                    borderRadius: theme.radii.lg,
                    backgroundColor:
                      theme.colorScheme === 'dark' ? 'rgba(24, 24, 24, 0.62)' : 'rgba(255, 255, 255, 0.55)',
                  },
                ]}
              />
            </>
          )}
          <View style={{ gap: theme.spacing(0.35) }}>
            <SubHeading style={{ letterSpacing: -0.1 }}>Start a trip</SubHeading>
            <Caption color={theme.colors.textSecondary}>Create the plan first, then add extras.</Caption>
          </View>

          <Button
            label="Plan manually (recommended)"
            icon={<Ionicons name="add-circle-outline" size={16} color={theme.colors.surface} />}
            onPress={onPlanManually}
            size="small"
            style={{ minHeight: 48 }}
          />
          <Button
            label="Plan with AI"
            variant="ghost"
            icon={<Ionicons name="sparkles-outline" size={16} color={theme.colors.primary} />}
            onPress={onPlanWithAI}
            size="small"
            style={{ minHeight: 44 }}
          />
          <Button
            label="Connect email"
            variant="ghost"
            icon={<Ionicons name="mail-outline" size={16} color={theme.colors.primary} />}
            onPress={onConnectEmail}
            size="small"
            style={{ minHeight: 44 }}
          />
          <Button
            label="Smart upload docs"
            variant="ghost"
            icon={<Ionicons name="cloud-upload-outline" size={16} color={theme.colors.primary} />}
            onPress={onSmartUpload}
            size="small"
            style={{ minHeight: 44 }}
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  glassCard: {
    width: '100%',
    alignSelf: 'center',
    borderWidth: 1,
    overflow: 'hidden',
  },
});


